import { customElement, property, state } from 'lit/decorators.js';
import { DashboardWidget } from './dashboard-widget';
import { DateRange } from './dashboard-filter';
import { generatedPurchaseOrdersCache } from '../data/generated-purchase-orders-cache';
import { html } from 'lit';
import { issuedQuotesCache } from '../data/issued-quotes-cache';
import { localDateTimeToServer } from '../../../../webmodule-common/other/datetime-converter';
import { PropertyValues } from 'lit';
import { PurchaseOrderState } from '../../../api/dealer-api-interface-franchisee';
import { QuoteState } from '../../../api/dealer-api-interface-quote';
import { Snippet } from '../../../../webmodule-common/interop/webmodule-interop';
import { LangSalesSuccessWidget } from '../../../../localization/scripts/dealer-franchisee/dashboard/widget/sales-success-widget-lang';

const ApexCharts = (globalThis as any).ApexCharts;

@customElement('sales-success-widget')
export class SalesSuccessWidget extends DashboardWidget {
  @state() private totalQuotes: number = 0;
  @state() private totalPurchaseOrders: number = 0;

  @property({ type: Object }) dateRange?: DateRange;
  @property() branchId?: string;
  @property() userId?: string;
  @property({ type: Array }) quoteStates: QuoteState[] = [
    QuoteState.Draft,
    QuoteState.Active,
    QuoteState.IssuePending,
    QuoteState.Issued,
    QuoteState.Accepted,
    QuoteState.Accepted_AssignedToReviewer,
    QuoteState.SupplierReviewPending,
    QuoteState.SupplierReviewed,
    QuoteState.Approved
  ];
  @property({ type: Array }) purchaseOrderStates: PurchaseOrderState[] = [
    PurchaseOrderState.Draft,
    PurchaseOrderState.IssuedPending,
    PurchaseOrderState.Issued,
    PurchaseOrderState.Completed
  ];

  private _quotesCache = issuedQuotesCache();
  private _purchaseOrdersCache = generatedPurchaseOrdersCache();

  chart: any | null = null;

  protected async refreshData(): Promise<void> {
    if (this.dateRange) {
      const startDate = localDateTimeToServer(this.dateRange.startDate);
      const endDate = localDateTimeToServer(this.dateRange.endDate);
      const quoteResult = await this._quotesCache?.getData(
        startDate,
        endDate,
        this.quoteStates,
        this.branchId,
        this.userId
      );
      if (quoteResult) {
        this.totalQuotes = quoteResult.revenueSummary.reduce((total, summary) => {
          return (total += summary.count);
        }, 0);
      }
      const purchaseOrderResult = await this._purchaseOrdersCache?.getData(
        startDate,
        endDate,
        this.purchaseOrderStates,
        this.branchId,
        this.userId
      );
      if (purchaseOrderResult) {
        this.totalPurchaseOrders = purchaseOrderResult.revenueSummary.reduce((total, summary) => {
          return (total += summary.count);
        }, 0);
      }
    }
  }

  protected firstUpdated(): void {
    const options = {
      series: [this.totalPurchaseOrders, this.unconvertedQuoteCount],
      labels: [LangSalesSuccessWidget.convertedQuote(), LangSalesSuccessWidget.unconvertedQuote()],
      colors: ['#3282CC', '#C7CBD0'],
      chart: {
        type: 'donut',
        // Sized to sit within the compact KPI card with breathing room; 140%
        // overflowed the card body (the donut touched the bottom border).
        height: '100%',
        offsetY: 0
      },
      dataLabels: {
        enabled: false
      },
      tooltip: {
        enabled: false
      },
      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: '60%',
            labels: {
              show: true,
              total: {
                show: true,
                showAlways: true,
                label: LangSalesSuccessWidget.convertedQuote(),
                formatter: () => this.getChartLabel()
              },
              name: {
                show: false
              },
              value: {
                show: true
              }
            }
          }
        }
      },
      legend: { show: false },
      states: {
        hover: {
          filter: {
            type: 'none',
            value: 0
          }
        },
        active: {
          filter: {
            type: 'none',
            value: 0
          }
        }
      }
    };
    this.chart = new ApexCharts(this.querySelector('.salesSuccessChart'), options);
    this.chart.render();
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    this.refreshData().then(() => {
      if (this.chart.series) {
        this.chart.updateSeries([this.totalPurchaseOrders, this.unconvertedQuoteCount], true);
      }
    });
  }

  protected getHeader(): Snippet {
    return LangSalesSuccessWidget.salesSuccess();
  }

  protected getBody(): Snippet {
    return html`
      <div class="container">
        <div class="row chart-widget-data-wrapper">
          <div class="col-6 chart-widget-data">
            <div class="display-5 text-primary">${html`${this.totalPurchaseOrders} / ${this.totalQuotes}`}</div>
            <div class="display-6 text-secondary">${LangSalesSuccessWidget.conversionQuoteToOrder()}</div>
          </div>
          <div class="col-6 chart-widget-chart">
            <div class="salesSuccessChart"></div>
          </div>
        </div>
      </div>
    `;
  }

  protected get unconvertedQuoteCount(): number {
    return this.totalQuotes - this.totalPurchaseOrders;
  }

  protected getChartLabel(): string {
    const result = ((this.totalPurchaseOrders + Number.EPSILON) / this.totalQuotes) * 100;
    const percentage = Math.round(result);
    // if there are currently no quotes in the system, we get a infinity result due to division by 0,
    // so check to make sure we have a finite result to return as a percentage.
    if (Number.isFinite(percentage)) {
      return `${percentage}%`;
    }
    return `0%`;
  }
}
